import React, { useCallback, useContext, useEffect, useState } from 'react';
import t from 'react-translate';
import { AngularContext } from 'react-app';
import { Button } from 'react-bootstrap';
import { useAppDispatch } from 'redux/store';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { disableHCMIntegration } from 'redux/actions/hcm';
import { HCMIntegrationTypes, HCMSettingsPayload } from 'redux/schemas/models/hcm';
import { wrapThunkAction } from 'redux/utils';
import ClickableContainer from 'components/clickable-container';
import { config } from '@config/pendo.config.json';
import NvTooltip from 'shared/components/nv-tooltip';
import SectionContent from '../section-content';
import HCMDataMapping from './hcm-data-mapping';
import HCMConfiguration from './hcm-configuration';

const HcmIntegration = () => {
  const { injectServices, $scope } = useContext(AngularContext);
  const [InstitutionsManager, AlertMessages] = injectServices(['InstitutionsManager', 'AlertMessages']);
  const [hcmEnabled, setHcmEnabled] = useState($scope.$eval('vm.InstitutionsManager.institution.novoedHcmEnabled'));
  const [hcmConfig, setHcmConfig] = useState($scope.$eval('vm.InstitutionsManager.institution.hcmConfig'));
  const [integrationErrors, setIntegrationErrors] = useState($scope.$eval('vm.InstitutionsManager.institution.integrationErrors'));
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showDataMapping, setShowDataMapping] = useState(false);

  const dispatch = useAppDispatch();

  useEffect(() => $scope.$watch('vm.InstitutionsManager.institution.novoedHcmEnabled', (newValue) => {
    setHcmEnabled(newValue);
  }), [$scope]);

  useEffect(() => $scope.$watch('vm.InstitutionsManager.institution.hcmConfig', (newValue) => {
    setHcmConfig(newValue);
  }), [$scope]);

  useEffect(() => $scope.$watch('vm.InstitutionsManager.institution.integrationErrors', (newValue) => {
    setIntegrationErrors(newValue);
  }), [$scope]);

  const hcmError = integrationErrors?.find(error => error.integrationType === 'workday_hcm');

  const hasHCMConfig = useCallback(() => {
    const { endpoint, username, password, integrationType } = hcmConfig;
    return endpoint && username && password && integrationType;
  }, [hcmConfig]);

  const disableHCM = () => {
    dispatch(openConfirmationDialog({
      title: t.INSTITUTIONS.ADVANCED_SETTINGS.HCM.DISABLE(),
      bodyText: t.INSTITUTIONS.ADVANCED_SETTINGS.HCM.DISABLE_DESCRIPTION(),
      confirmText: t.FORM.YES_SURE(),
      cancelDataQa: config.pendo.hcm.integrationCancelDisable,
      confirmDataQa: config.pendo.hcm.integrationConfirmDisable,
      onConfirm: () => {
        const hcmConfigData = {
          endpoint: '',
          username: '',
          password: '',
          integrationType: '',
        };

        wrapThunkAction(
          dispatch(
            disableHCMIntegration({
              institutionId: InstitutionsManager.institution.id,
              ...hcmConfigData,
            } as HCMSettingsPayload),
          ),
        ).then(() => {
          InstitutionsManager.institution.updateFromReact({ hcmConfig: hcmConfigData });
          setHcmConfig(hcmConfigData);
        });
      },
    }));
  };

  let ctaText = t.INSTITUTIONS.ADVANCED_SETTINGS.CONFIGURE();
  if (hcmEnabled && hasHCMConfig()) {
    ctaText = hcmConfig
      ?.integrationType === HCMIntegrationTypes.CSV_SFTP
      ? t.INSTITUTIONS.ADVANCED_SETTINGS.HCM.DISPLAY_DETAILS()
      : t.INSTITUTIONS.ADVANCED_SETTINGS.HCM.EDIT();
  }

  return (
    <React.Fragment>
      <SectionContent
        header={t.INSTITUTIONS.ADVANCED_SETTINGS.HCM.TITLE()}
        description={[t.INSTITUTIONS.ADVANCED_SETTINGS.HCM.DESCRIPTION()]}
        extras={(
          <React.Fragment>
            {hcmEnabled && (
              <div className='d-flex text-regular align-self-start'>
                <span>
                  {t.INSTITUTIONS.ADVANCED_SETTINGS.HCM.CSV_DESCRIPTION()}
                </span>
                <ClickableContainer
                  className='text-primary ml-2'
                  onClick={() => setShowDataMapping(true)}
                >
                  {t.INSTITUTIONS.ADVANCED_SETTINGS.HCM.CLICK_HERE()}
                </ClickableContainer>
              </div>
            )}
            {hcmError && (
              <div className='d-flex align-items-center text-danger mt-8'>
                <span className='icon icon-xss-smallest icon-warning mr-1' aria-hidden='true' />
                <span className='text-small text-center'>
                  {t.INTEGRATION_ERRORS.HCM.SYNC_FAILED()}
                </span>
              </div>
            )}
            <div className='button-bar mt-6'>
              {hcmEnabled && hasHCMConfig() && (
                <Button
                  variant='secondary'
                  onClick={disableHCM}
                  data-qa={config.pendo.hcm.integrationDisable}
                >
                  {t.INSTITUTIONS.ADVANCED_SETTINGS.HCM.DISABLE()}
                </Button>
              )}
              <NvTooltip
                enabled={!hcmEnabled}
                text={t.INSTITUTIONS.ADVANCED_SETTINGS.HCM.CONFIGURE_TOOLTIP()}
                placement='top'
              >
                <Button
                  variant='primary'
                  onClick={() => setShowConfigModal(true)}
                  data-qa={hasHCMConfig()
                    ? config.pendo.hcm.integrationEdit
                    : config.pendo.hcm.integrationConfigure}
                  disabled={!hcmEnabled}
                >
                  {ctaText}
                </Button>
              </NvTooltip>
            </div>
          </React.Fragment>
        )}
      />
      <HCMConfiguration
        show={showConfigModal}
        onClose={(data) => {
          setShowConfigModal(false);
          if (data?.integrationType) {
            setHcmConfig(data);
            if (hcmError) {
              setIntegrationErrors([]);
              AlertMessages.arrayOfMessages()
                .filter(m => m.uniqueId?.startsWith('integration-error-'))
                .forEach(m => AlertMessages.remove(m));
            }
          }
        }}
        hcmConfig={hcmConfig}
      />
      <HCMDataMapping
        show={showDataMapping}
        onClose={() => setShowDataMapping(false)}
      />
    </React.Fragment>
  );
};

export default HcmIntegration;
